/**
 * The Campaign Summary dashboard's panels.
 *
 * Split out of the page so the page file is layout and wiring only. Each
 * export here is one grid cell; none of them own data fetching — everything
 * arrives as props so the panels stay trivially testable and the page keeps a
 * single source of truth for the feed.
 */
import { memo, type JSX } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import type { SupervisorAgentSummary, SupervisorLiveCall } from '@kc3/types'
import {
  DashPanel,
  StatTile,
  StatusCell,
  Elapsed,
  LiveCallTimer,
  WrapUpCountdown,
  Avatar,
  BarNum,
  MeterPct,
  Rank,
  Dot,
  Num,
  TABLE_PT,
  TH,
  TD,
  type Band,
  type StatusTone,
} from './ui.js'
import {
  formatDurationShort,
  formatInt,
  formatPercent,
  formatMetric,
  formatBucketLabel,
  slBand,
  abandonBand,
} from './format.js'
import type {
  AgentRow,
  CampaignRow,
  DispositionRow,
  IntervalRow,
  PivotRow,
  QueueRow,
  SummaryMetrics,
} from './types.js'
import type { CallAction, SupervisorActions } from './actions.js'

const EMPTY_TABLE_PROPS = {
  size: 'small' as const,
  scrollable: true,
  scrollHeight: 'flex' as const,
  removableSort: true,
  pt: TABLE_PT,
  className: 'h-full',
}

/**
 * One button inside a `.kc-actions` segmented group.
 *
 * `text` + `rounded={false}` keep Lara from painting a filled, pill-shaped
 * button inside the strip; the strip's own CSS owns borders and radius.
 */
function ActionButton({
  icon,
  label,
  onClick,
  disabled = false,
  tone,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled?: boolean
  tone?: 'danger' | 'warn'
}): JSX.Element {
  const toneClass =
    tone === 'danger' ? 'kc-act-danger' : tone === 'warn' ? 'kc-act-warn' : ''
  return (
    <Button
      icon={icon}
      text
      rounded={false}
      className={toneClass}
      aria-label={label}
      tooltip={label}
      tooltipOptions={{
        position: 'top',
        showDelay: 300,
        // Tooltips portal to document.body, outside `.kc-cs` — this class is
        // how the page's styling reaches them at all. Long labels (the
        // disabled-reason strings) wrap; short verbs stay on one line.
        className: `kc-cs-tip${label.length > 24 ? ' kc-tip-wrap' : ''}`,
        // `mouseTrack` would drag the bubble around under the cursor inside a
        // 28px button — distracting at this density.
        showOnDisabled: true,
      }}
      disabled={disabled}
      onClick={onClick}
    />
  )
}

/** The bordered strip that groups a row's actions into one control. */
function ActionGroup({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="kc-actions">{children}</div>
}

/* ── KPI strip ─────────────────────────────────────────────────────────── */

export function KpiPanel({
  agents,
  calls,
  totals,
}: {
  agents: SupervisorAgentSummary[]
  calls: SupervisorLiveCall[]
  totals: SummaryMetrics
}): JSX.Element {
  const loggedIn = agents.filter((a) => a.status !== 'OFFLINE').length
  const onCall = agents.filter((a) => a.status === 'IN_CALL').length
  const paused = agents.filter(
    (a) => a.status === 'PAUSED' || a.status === 'ON_BREAK',
  ).length
  const availableAgents = agents.filter(
    (a) => a.status === 'IDLE' || a.status === 'AVAILABLE_ON_DEMAND',
  )
  const available = availableAgents.length

  // The EARLIEST timestamp, not a computed elapsed value. Storing the instant
  // lets <Elapsed> advance the tile on its own; storing a duration would force
  // this whole panel to re-render every second to recompute it.
  const availableSince = availableAgents.reduce<string | undefined>((earliest, a) => {
    if (!a.statusSince) return earliest
    return !earliest || a.statusSince < earliest ? a.statusSince : earliest
  }, undefined)

  // "Waiting" is a call with no agent talking on it yet — the gateway emits
  // RINGING sessions into the same list as connected ones.
  const waiting = calls.filter((c) => c.durationSec === 0).length

  // Same idea: the oldest live call is a stable fact; how long it has been up
  // is derived by <Elapsed>.
  const oldestCallAt = calls.reduce<string | undefined>((earliest, c) => {
    return !earliest || c.startedAt < earliest ? c.startedAt : earliest
  }, undefined)

  return (
    <DashPanel title="Live Overview" flush>
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 p-3 h-full items-stretch overflow-auto">
        <StatTile label="Logged In" value={loggedIn} sub={`${agents.length} total`} />
        <StatTile
          label="Available"
          value={available}
          // Longest CURRENT availability, not a cumulative total: the useful
          // question at a glance is "has someone been sitting free for a
          // while", which a sum across agents cannot answer.
          sub={
            availableSince ? (
              <span>
                longest <Elapsed from={availableSince} variant="short" />
              </span>
            ) : undefined
          }
          band={available > 0 ? 'good' : 'neutral'}
        />
        <StatTile label="On Call" value={onCall} />
        <StatTile label="Paused" value={paused} band={paused > 0 ? 'warn' : 'neutral'} />
        <StatTile label="Active Calls" value={calls.length} />
        <StatTile label="Waiting" value={waiting} band={waiting > 0 ? 'warn' : 'neutral'} />
        <StatTile
          label="Longest Wait"
          value={
            oldestCallAt ? (
              <Elapsed from={oldestCallAt} bandAfterSec={60} bandTo="bad" />
            ) : (
              '0:00'
            )
          }
        />
        <StatTile
          label="Service Level"
          value={formatPercent(totals.serviceLevelPct)}
          sub={`${formatInt(totals.answeredTotal)} answered`}
          band={slBand(totals.serviceLevelPct)}
        />
      </div>
    </DashPanel>
  )
}

/* ── Live agents ───────────────────────────────────────────────────────── */

export function AgentsPanel({
  agents,
  calls,
  actions,
  canForceLogout,
}: {
  agents: SupervisorAgentSummary[]
  calls: SupervisorLiveCall[]
  actions: SupervisorActions
  canForceLogout: boolean
}): JSX.Element {
  // agentId → the call they are on right now, so the Call Time column can tick
  // off a real `startedAt` instead of showing the cumulative daily figure.
  const callByAgent = new Map<string, SupervisorLiveCall>()
  for (const c of calls) callByAgent.set(c.agentId, c)

  // OFFLINE agents are filtered out — the operator's ask: a logged-out agent
  // holding a row with a 15-hour "In State" timer reads as live data it is
  // not. They still count in the KPI tile's "N total" denominator.
  const visibleAgents = agents.filter((a) => a.status !== 'OFFLINE')

  return (
    <DashPanel title="Agents" count={visibleAgents.length} flush>
      <DataTable
        {...EMPTY_TABLE_PROPS}
        value={visibleAgents}
        dataKey="id"
        sortField="name"
        sortOrder={1}
        emptyMessage="No agents online."
      >
        <Column
          field="name"
          header="Agent"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => (
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={a.name} />
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800">{a.name}</div>
                {a.email && (
                  <div className="truncate text-[10px] text-slate-400">{a.email}</div>
                )}
              </div>
            </div>
          )}
        />
        <Column
          field="extension"
          header="Ext"
          sortable
          headerClassName={TH}
          bodyClassName={`${TD} font-mono`}
          body={(a: SupervisorAgentSummary) => a.extension ?? '—'}
        />
        <Column
          field="status"
          header="Status"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => (
            <StatusCell
              status={a.status}
              {...(a.statusReason ? { reason: a.statusReason } : {})}
            />
          )}
        />
        {/* How long they have been in that state. Recomputed from
            `statusSince` on every render so the 1s tick advances it, rather
            than showing a duration frozen at the last push. Sorting is on the
            raw timestamp, which orders correctly and inversely to the
            displayed elapsed time — oldest state first. */}
        <Column
          field="statusSince"
          header="In State"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => {
            // Only idle-ish states escalate: an agent five minutes into a CALL
            // is working; five minutes into a break is what a supervisor is
            // scanning for.
            const idleState =
              a.status === 'PAUSED' || a.status === 'ON_BREAK' || a.status === 'ACW'
            // Wrap-up (ACW) shows a COUNTDOWN to the deadline rather than
            // elapsed-since, so a supervisor can see how long the agent has
            // left before they auto-return to Idle. The countdown is driven
            // by `wrapUpEndsAt` (the Redis wrap_up_until key); when it is
            // absent the elapsed-since display still applies, which is the
            // correct fallback for an ACW entered without a timer.
            if (a.status === 'ACW' && a.wrapUpEndsAt) {
              return (
                <span className="block text-right">
                  <WrapUpCountdown until={a.wrapUpEndsAt} />
                </span>
              )
            }
            return (
              <span className="block text-right">
                <Elapsed
                  from={a.statusSince}
                  {...(idleState ? { bandAfterSec: 300, bandTo: 'warn' as const } : {})}
                />
              </span>
            )
          }}
        />
        {/* Live duration of the call the agent is on RIGHT NOW, counting up.
            Distinct from Talk Time below, which is the cumulative total for the
            day and only moves when a call ends — the reference draws the same
            distinction, showing a running timer only while the agent is
            actually connected. */}
        <Column
          header="Call Time"
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => {
            const call = callByAgent.get(a.id)
            if (!call) return <Num muted>—</Num>
            return (
              <span className="flex justify-end">
                <LiveCallTimer from={call.startedAt} />
              </span>
            )
          }}
        />
        <Column
          field="callsHandled"
          header="Calls"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => (
            <Num muted={a.callsHandled === 0}>{formatInt(a.callsHandled)}</Num>
          )}
        />
        <Column
          field="callDurationSec"
          header="Talk Time"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(a: SupervisorAgentSummary) => (
            <Num muted={a.callDurationSec === 0}>
              {formatDurationShort(a.callDurationSec)}
            </Num>
          )}
        />
        {/* Actions mirror the KC2 reference's agent row: the three call-control
            actions are live only while the agent is actually on a call, and
            logout is blocked while they are — the reference gates them the same
            way, and forcing a logout mid-call would drop a live customer. */}
        <Column
          header="Actions"
          headerClassName={`${TH} text-right`}
          bodyClassName={TD}
          style={{ width: '1%' }}
          body={(a: SupervisorAgentSummary) => {
            const onCall = a.status === 'IN_CALL' && Boolean(a.currentCallId)
            const offline = a.status === 'OFFLINE'
            // AGENT-scoped actions only.
            //
            // The call controls (listen / coach / barge) used to be duplicated
            // here as well as in Active Calls, so the same headphones icon
            // appeared twice on one screen for the same call — two places to
            // click, no indication they did the same thing. Call controls now
            // live exclusively in Active Calls, which is the panel that is
            // actually ABOUT calls; this column is about the agent.
            return (
              <div className="flex justify-end">
                <ActionGroup>
                  <ActionButton
                    icon="pi pi-pause"
                    label={`Force pause ${a.name}`}
                    disabled={a.status === 'PAUSED' || offline}
                    tone="warn"
                    // userId, NOT id — the worker's agent-state seam keys
                    // presence on the user id. See SupervisorAgentSummary.userId.
                    onClick={() => actions.forcePause(a.userId, a.name)}
                  />
                  <ActionButton
                    icon="pi pi-sign-out"
                    label={
                      !canForceLogout
                        ? 'Force logout — requires the agents:force_logout permission'
                        : onCall
                          ? 'Force logout — agent is on a call'
                          : `Force logout ${a.name}`
                    }
                    disabled={!canForceLogout || offline || onCall}
                    tone="danger"
                    onClick={() => actions.forceLogout(a.userId, a.name)}
                  />
                </ActionGroup>
              </div>
            )
          }}
        />
      </DataTable>
    </DashPanel>
  )
}

/* ── Live calls ────────────────────────────────────────────────────────── */

export interface CallControl {
  action: CallAction
  icon: string
  label: string
  tone?: 'warn' | 'danger'
}

const CALL_CONTROLS: ReadonlyArray<CallControl> = [
  { action: 'listen', icon: 'pi pi-headphones', label: 'Listen' },
  { action: 'whisper', icon: 'pi pi-megaphone', label: 'Whisper' },
  { action: 'coach', icon: 'pi pi-comments', label: 'Coach' },
  { action: 'barge', icon: 'pi pi-users', label: 'Barge', tone: 'warn' },
  { action: 'park', icon: 'pi pi-inbox', label: 'Park' },
  { action: 'conference', icon: 'pi pi-user-plus', label: 'Conference' },
]

/**
 * The full control surface the API actually registers, including the four
 * actions the demo dashboard's strip omits (unpark, hold, unhold).
 *
 * Every entry here maps to a route in `apps/api/src/routes/supervisor/actions.ts`
 * and is gated by its own permission there, so a supervisor who lacks one gets a
 * 403 with an explanatory toast rather than a hidden button — which is the
 * behaviour you want when roles are being configured.
 */
export const ALL_CALL_CONTROLS: ReadonlyArray<CallControl> = [
  { action: 'listen', icon: 'pi pi-headphones', label: 'Listen' },
  { action: 'whisper', icon: 'pi pi-megaphone', label: 'Whisper' },
  { action: 'coach', icon: 'pi pi-comments', label: 'Coach' },
  { action: 'barge', icon: 'pi pi-users', label: 'Barge', tone: 'warn' },
  { action: 'hold', icon: 'pi pi-pause-circle', label: 'Hold' },
  { action: 'unhold', icon: 'pi pi-play-circle', label: 'Unhold' },
  { action: 'park', icon: 'pi pi-inbox', label: 'Park' },
  { action: 'unpark', icon: 'pi pi-upload', label: 'Unpark' },
  { action: 'conference', icon: 'pi pi-user-plus', label: 'Conference' },
]

/**
 * Live call status → the same pill treatment the agent StatusCell uses.
 * ON_HOLD is the one that matters most — the action that caused it (a
 * supervisor or agent hold) happened silently at the media layer, so this
 * cell is the only place the dashboard admits the call is parked on music.
 */
const CALL_STATE_META: Record<
  NonNullable<SupervisorLiveCall['status']>,
  { tone: StatusTone; label: string }
> = {
  RINGING: { tone: 'transition', label: 'Ringing' },
  ANSWERED: { tone: 'active', label: 'Live' },
  ON_HOLD: { tone: 'away', label: 'On Hold' },
}

function CallStateCell({
  status,
}: {
  status: SupervisorLiveCall['status']
}): JSX.Element {
  const meta = CALL_STATE_META[status ?? 'ANSWERED']
  return (
    <span className={`kc-status kc-status-${meta.tone}`}>
      <span
        className={`kc-dot kc-status-dot${meta.tone === 'transition' ? ' kc-status-dot-live' : ''}`}
      />
      {meta.label}
    </span>
  )
}

export function CallsPanel({
  calls,
  queueNames,
  actions,
  onListen,
  controls = CALL_CONTROLS,
}: {
  calls: SupervisorLiveCall[]
  queueNames: Map<string, string>
  actions: SupervisorActions
  /** DEMO MODE ONLY (phase 1) — plays the recording behind a simulated call. */
  onListen?: (callId: string) => void
  /** Defaults to the demo dashboard's strip; the live page passes {@link ALL_CALL_CONTROLS}. */
  controls?: ReadonlyArray<CallControl>
}): JSX.Element {
  return (
    <DashPanel title="Active Calls" count={calls.length} flush>
      <DataTable
        {...EMPTY_TABLE_PROPS}
        value={calls}
        dataKey="callId"
        emptyMessage="No active calls."
      >
        <Column
          field="agentName"
          header="Agent"
          sortable
          headerClassName={TH}
          bodyClassName={`${TD} font-medium`}
        />
        <Column
          field="callerCLI"
          header="Caller"
          sortable
          headerClassName={TH}
          bodyClassName={`${TD} font-mono`}
        />
        <Column
          header="Queue"
          sortable
          field="queueId"
          headerClassName={TH}
          bodyClassName={TD}
          body={(c: SupervisorLiveCall) =>
            c.queueId ? (queueNames.get(c.queueId) ?? c.queueId) : '—'
          }
        />
        <Column
          header="State"
          field="status"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(c: SupervisorLiveCall) => <CallStateCell status={c.status} />}
        />
        <Column
          header="Duration"
          sortable
          field="startedAt"
          headerClassName={TH}
          bodyClassName={TD}
          body={(c: SupervisorLiveCall) => (
            // Derived from startedAt rather than the pushed `durationSec`, and
            // rendered by <Elapsed> so it advances without re-rendering the row.
            <span className="flex justify-end">
              <LiveCallTimer from={c.startedAt} />
            </span>
          )}
        />
        <Column
          header="Actions"
          headerClassName={`${TH} text-right`}
          bodyClassName={TD}
          style={{ width: '1%' }}
          body={(c: SupervisorLiveCall) => (
            <div className="flex justify-end">
              <ActionGroup>
                {controls.map((ctrl) => (
                  <ActionButton
                    key={ctrl.action}
                    icon={ctrl.icon}
                    label={ctrl.label}
                    {...(ctrl.tone ? { tone: ctrl.tone } : {})}
                    // A held call can only be unheld; holding again is a no-op
                    // the backend would ack while nothing changes. Same idea
                    // inverted for unhold.
                    disabled={
                      ctrl.action === 'hold'
                        ? c.status === 'ON_HOLD'
                        : ctrl.action === 'unhold'
                          ? c.status !== 'ON_HOLD'
                          : false
                    }
                    onClick={() =>
                      ctrl.action === 'listen' && onListen
                        ? onListen(c.callId)
                        : actions.invokeCallAction(ctrl.action, c.callId)
                    }
                  />
                ))}
              </ActionGroup>
            </div>
          )}
        />
      </DataTable>
    </DashPanel>
  )
}

/* ── Agent state distribution ──────────────────────────────────────────── */

function StateCountPanelImpl({
  agents,
}: {
  agents: SupervisorAgentSummary[]
}): JSX.Element {
  // OFFLINE is excluded for the same reason the Agents table hides those
  // rows: it is a non-state for a wallboard — an agent with no session, not
  // an agent doing something.
  const online = agents.filter((a) => a.status !== 'OFFLINE')
  const counts = new Map<string, number>()
  for (const a of online) counts.set(a.status, (counts.get(a.status) ?? 0) + 1)

  const total = online.length
  const rows = Array.from(counts.entries())
    .map(([status, count]) => ({ status, count, pct: total ? (count / total) * 100 : 0 }))
    .sort((a, b) => b.count - a.count)

  return (
    <DashPanel title="Agent States" count={total} flush>
      <div className="h-full overflow-auto p-2 flex flex-col gap-1.5">
        {rows.length === 0 && (
          <div className="text-[12px] text-slate-400 p-2">No agents online.</div>
        )}
        {rows.map((r) => (
          <div key={r.status} className="kc-row min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <StatusCell status={r.status as SupervisorAgentSummary['status']} />
              <span className="text-[12px] font-semibold tabular-nums text-slate-700">
                {r.count}
                <span className="ml-1 text-[10px] font-normal text-slate-400">
                  {Math.round(r.pct)}%
                </span>
              </span>
            </div>
            {/* A bar, not a chart library: one dimension, ten rows, and it has
                to stay readable inside a panel the user can resize to 2 cells. */}
            <div className="kc-meter">
              <div className="kc-meter-fill" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </DashPanel>
  )
}

/* ── Live queue activity ───────────────────────────────────────────────── */

function LiveQueuePanelImpl({
  agents,
  calls,
  queueNames,
}: {
  agents: SupervisorAgentSummary[]
  calls: SupervisorLiveCall[]
  queueNames: Map<string, string>
}): JSX.Element {
  const queueIds = new Set<string>()
  for (const a of agents) if (a.currentQueueId) queueIds.add(a.currentQueueId)
  for (const c of calls) if (c.queueId) queueIds.add(c.queueId)

  const rows = Array.from(queueIds)
    .map((queueId) => {
      const queueCalls = calls.filter((c) => c.queueId === queueId)
      // Oldest call in this queue, as an instant — <Elapsed> counts it up.
      const oldestAt = queueCalls.reduce<string | undefined>((earliest, c) => {
        return !earliest || c.startedAt < earliest ? c.startedAt : earliest
      }, undefined)
      return {
        queueId,
        queueName: queueNames.get(queueId) ?? queueId,
        agents: agents.filter((a) => a.currentQueueId === queueId).length,
        onCall: agents.filter(
          (a) => a.currentQueueId === queueId && a.status === 'IN_CALL',
        ).length,
        calls: queueCalls.length,
        waiting: queueCalls.filter((c) => c.durationSec === 0).length,
        oldestAt,
      }
    })
    .sort((a, b) => a.queueName.localeCompare(b.queueName))

  return (
    <DashPanel title="Live Queues" count={rows.length} flush>
      <DataTable
        {...EMPTY_TABLE_PROPS}
        value={rows}
        dataKey="queueId"
        emptyMessage="No queue activity."
      >
        <Column field="queueName" header="Queue" sortable headerClassName={TH} bodyClassName={`${TD} font-medium`} />
        <Column field="agents" header="Agents" sortable headerClassName={TH} bodyClassName={TD} body={(r) => <Num>{r.agents}</Num>} />
        <Column field="onCall" header="On Call" sortable headerClassName={TH} bodyClassName={TD} body={(r) => <Num>{r.onCall}</Num>} />
        <Column field="calls" header="Calls" sortable headerClassName={TH} bodyClassName={TD} body={(r) => <Num>{r.calls}</Num>} />
        <Column field="waiting" header="Waiting" sortable headerClassName={TH} bodyClassName={TD} body={(r) => <Num band={r.waiting > 0 ? 'warn' : 'neutral'}>{r.waiting}</Num>} />
        <Column
          field="oldestAt"
          header="Longest"
          sortable
          headerClassName={TH}
          bodyClassName={TD}
          body={(r) => (
            <span className="block text-right">
              <Elapsed from={r.oldestAt} bandAfterSec={60} bandTo="bad" />
            </span>
          )}
        />
      </DataTable>
    </DashPanel>
  )
}

/* ── Interval pivot ────────────────────────────────────────────────────── */

function IntervalPivotPanelImpl({
  intervals,
  pivot,
  totals,
  actions,
}: {
  intervals: IntervalRow[]
  pivot: PivotRow[]
  totals: SummaryMetrics
  actions?: JSX.Element
}): JSX.Element {
  return (
    <DashPanel
      title="Campaign Summary by Interval"
      count={intervals.length}
      {...(actions ? { actions } : {})}
      flush
    >
      <DataTable
        {...EMPTY_TABLE_PROPS}
        value={pivot}
        dataKey="key"
        emptyMessage="No calls in this window."
      >
        <Column
          field="label"
          header="Call Details"
          frozen
          headerClassName={`${TH} !bg-slate-100`}
          bodyClassName={`${TD} font-medium !bg-white whitespace-nowrap`}
          style={{ minWidth: '15rem' }}
        />
        {/* Total column sits FIRST after the label, not last: it is the number
            the supervisor actually acts on, and pushing it past a dozen
            scrolling buckets hides it. */}
        <Column
          header="Total"
          headerClassName={`${TH} !bg-slate-100`}
          bodyClassName={`${TD} !bg-slate-50/60 font-semibold`}
          style={{ minWidth: '6rem' }}
          body={(row: PivotRow) => (
            <Num>{formatMetric(Number(totals[row.key] ?? 0), row.format)}</Num>
          )}
        />
        {intervals.map((iv) => (
          <Column
            key={iv.bucketStart}
            header={formatBucketLabel(iv.bucketStart)}
            headerClassName={TH}
            bodyClassName={TD}
            style={{ minWidth: '6rem' }}
            body={(row: PivotRow) => {
              const value = row.values[iv.bucketStart] ?? 0
              let band: Band = 'neutral'
              if (row.key === 'serviceLevelPct') band = slBand(value)
              if (row.key === 'abandonRatePct' || row.key === 'ivrAbandonRatePct') {
                band = abandonBand(value)
              }
              return (
                <Num band={band} muted={value === 0}>
                  {formatMetric(value, row.format)}
                </Num>
              )
            }}
          />
        ))}
      </DataTable>
    </DashPanel>
  )
}

/* ── Shared performance columns ────────────────────────────────────────── */

/**
 * The metric columns every historical table shares.
 *
 * Returned as an array of `<Column>` rather than a component: PrimeReact's
 * DataTable reads its children's props directly and silently ignores any child
 * that is not a Column, so wrapping these in a component would render nothing.
 */
function metricColumns(rows: SummaryMetrics[]): JSX.Element[] {
  // Bars are scaled to the largest value in the SAME column so a quiet window
  // still produces a readable shape. Guarded at 1 so an all-zero column cannot
  // divide by zero and render NaN-width bars.
  const maxCalls = Math.max(1, ...rows.map((r) => r.totalCalls))
  const maxAnswered = Math.max(1, ...rows.map((r) => r.answeredTotal))
  const maxTalk = Math.max(1, ...rows.map((r) => r.totalTalkSec))
  return [
    <Column key="total" field="totalCalls" header="Calls" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <BarNum value={r.totalCalls} max={maxCalls} format={formatInt} />} />,
    <Column key="in" field="totalInbound" header="In" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num muted={r.totalInbound === 0}>{formatInt(r.totalInbound)}</Num>} />,
    <Column key="out" field="totalOutbound" header="Out" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num muted={r.totalOutbound === 0}>{formatInt(r.totalOutbound)}</Num>} />,
    <Column key="ans" field="answeredTotal" header="Answered" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <BarNum value={r.answeredTotal} max={maxAnswered} format={formatInt} band="good" />} />,
    <Column key="aband" field="abandonedTotal" header="Aband." sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num band={r.abandonedTotal > 0 ? 'warn' : 'neutral'} muted={r.abandonedTotal === 0}>{formatInt(r.abandonedTotal)}</Num>} />,
    <Column key="abandpct" field="abandonRatePct" header="Aband. %" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <MeterPct pct={r.abandonRatePct} band={abandonBand(r.abandonRatePct)} />} />,
    <Column key="asa" field="avgAnswerSpeedSec" header="ASA" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num>{formatDurationShort(r.avgAnswerSpeedSec)}</Num>} />,
    <Column key="aht" field="avgHandleSec" header="AHT" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num>{formatDurationShort(r.avgHandleSec)}</Num>} />,
    <Column key="talk" field="totalTalkSec" header="Talk" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <BarNum value={r.totalTalkSec} max={maxTalk} format={formatDurationShort} />} />,
    <Column key="hold" field="totalHoldSec" header="Hold" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <Num muted={r.totalHoldSec === 0}>{formatDurationShort(r.totalHoldSec)}</Num>} />,
    <Column key="sl" field="serviceLevelPct" header="SL %" sortable headerClassName={TH} bodyClassName={TD} body={(r: SummaryMetrics) => <MeterPct pct={r.serviceLevelPct} band={slBand(r.serviceLevelPct)} />} />,
  ]
}

function AgentPerformancePanelImpl({ rows }: { rows: AgentRow[] }): JSX.Element {
  return (
    <DashPanel title="Agent Performance" count={rows.length} flush>
      <DataTable {...EMPTY_TABLE_PROPS} value={rows} dataKey="agentId" emptyMessage="No agent activity in this window.">
        <Column
          field="agentName"
          header="Agent"
          sortable
          frozen
          headerClassName={`${TH} !bg-slate-100`}
          bodyClassName={`${TD} font-medium !bg-white`}
          style={{ minWidth: '13.5rem' }}
          body={(r: AgentRow, opts) => (
            <div className="flex items-center gap-2 min-w-0">
              {/* rowIndex, not a stored field: the aggregator returns rows
                  ordered by volume, and re-sorting the table by another column
                  should renumber the badges to match what is on screen. */}
              <Rank index={opts.rowIndex} />
              <Avatar name={r.agentName} />
              <span className="truncate">{r.agentName}</span>
            </div>
          )}
        />
        {metricColumns(rows)}
      </DataTable>
    </DashPanel>
  )
}

function QueuePerformancePanelImpl({ rows }: { rows: QueueRow[] }): JSX.Element {
  return (
    <DashPanel title="Queue Performance" count={rows.length} flush>
      <DataTable {...EMPTY_TABLE_PROPS} value={rows} dataKey="queueId" emptyMessage="No queue activity in this window.">
        <Column
          field="queueName"
          header="Queue"
          sortable
          frozen
          headerClassName={`${TH} !bg-slate-100`}
          bodyClassName={`${TD} font-medium !bg-white`}
          style={{ minWidth: '13rem' }}
          body={(r: QueueRow) => (
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={r.queueName} />
              <div className="min-w-0">
                <div className="truncate">{r.queueName}</div>
                <div className="truncate text-[10px] text-slate-400 tabular-nums">
                  {r.slTargetSec}s target
                </div>
              </div>
            </div>
          )}
        />
        {metricColumns(rows)}
      </DataTable>
    </DashPanel>
  )
}

function CampaignPerformancePanelImpl({ rows }: { rows: CampaignRow[] }): JSX.Element {
  return (
    <DashPanel title="Campaign Performance" count={rows.length} flush>
      <DataTable {...EMPTY_TABLE_PROPS} value={rows} dataKey="campaignId" emptyMessage="No campaign activity in this window.">
        <Column
          field="campaignName"
          header="Campaign"
          sortable
          frozen
          headerClassName={`${TH} !bg-slate-100`}
          bodyClassName={`${TD} font-medium !bg-white`}
          style={{ minWidth: '13rem' }}
          body={(r: CampaignRow) => (
            <div className="flex items-center gap-2 min-w-0">
              <Avatar name={r.campaignName} />
              <div className="min-w-0">
                <div className="truncate">{r.campaignName}</div>
                {/* DID — the inbound numbers routed to this campaign. Shown
                    under the name (mirrors the KC2 reference's campaign+DID
                    column) and muted so the campaign name stays the primary
                    read. Empty array and null both render as '—'. */}
                <div className="truncate text-[10px] text-slate-400 tabular-nums">
                  {r.dids && r.dids.length > 0 ? r.dids.join(', ') : '—'}
                </div>
              </div>
            </div>
          )}
        />
        {metricColumns(rows)}
      </DataTable>
    </DashPanel>
  )
}

/* ── Dispositions ──────────────────────────────────────────────────────── */

function DispositionPanelImpl({ rows }: { rows: DispositionRow[] }): JSX.Element {
  const max = rows.reduce((m, r) => (r.count > m ? r.count : m), 0)

  return (
    <DashPanel title="Dispositions" count={rows.length} flush>
      <div className="h-full overflow-auto p-2 flex flex-col gap-1.5">
        {rows.length === 0 && (
          <div className="text-[12px] text-slate-400 p-2">
            No calls in this window.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.disposition} className="min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <Dot
                band={r.disposition === 'Not Dispositioned' ? 'warn' : 'neutral'}
                label={r.disposition}
              />
              <span className="text-[12px] tabular-nums text-slate-700 shrink-0">
                {formatInt(r.count)}
                <span className="ml-1 text-slate-400">{formatPercent(r.pct)}</span>
              </span>
            </div>
            <div className="mt-1 h-1 w-full rounded-[3px] bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-[3px] bg-slate-400"
                style={{ width: `${max > 0 ? (r.count / max) * 100 : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </DashPanel>
  )
}


/* ── Memoised exports ──────────────────────────────────────────────────────
 * The page re-renders once a second so the live call/state timers stay smooth.
 * These panels do NOT change at that rate: the historical tables are rebuilt
 * every few seconds and the live-derived ones only when their agent/call arrays
 * actually change identity. Without memo, one clock tick re-rendered seven
 * PrimeReact DataTables — each rebuilding every cell body — which is what made
 * the dashboard feel sluggish and made dragging a panel stutter.
 *
 * Default shallow prop comparison is exactly right here: every prop is either a
 * primitive or an array/object whose identity is already stabilised by a
 * `useMemo` upstream.
 */
export const IntervalPivotPanel = memo(IntervalPivotPanelImpl)
export const AgentPerformancePanel = memo(AgentPerformancePanelImpl)
export const QueuePerformancePanel = memo(QueuePerformancePanelImpl)
export const CampaignPerformancePanel = memo(CampaignPerformancePanelImpl)
export const DispositionPanel = memo(DispositionPanelImpl)
export const StateCountPanel = memo(StateCountPanelImpl)
export const LiveQueuePanel = memo(LiveQueuePanelImpl)
